import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import EDDialog from '../../../../../../../../BaseComponents/Dialog/EDDialog';
import EDLink from '../../../../../../../../BaseComponents/Link/EDLink';
import { EDTextEdit } from '../../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../../../BaseComponents/Typography/EDTypography';
import {
  RequestNewPwd,
  RequestNewPwdVariables,
} from './Mutations/_QueryTypes/RequestNewPwd';
import RequestNewPwdMutationDoc from './Mutations/RequestNewPwd.gql';
import { TEDSYSUserPwdResponse } from '../../../../../../../../globalQueryTypes';
import EDKeybind from '../../../../../../../../BaseComponents/Keybinds/EDKeybind';

interface IProps {
  gas?: boolean;
  dbname?: string;
  email?: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ResetPwdDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const [epost, setEpost] = useState(props.email ?? '');

  const [requestNewPwdQuery, { data, error, loading }] = useMutation<
    RequestNewPwd,
    RequestNewPwdVariables
  >(RequestNewPwdMutationDoc);

  const requestNewPwd = useCallback(() => {
    if (props.dbname) {
      requestNewPwdQuery({ variables: { dbname: props.dbname, email: epost } });
    }
  }, [requestNewPwdQuery, props.dbname, epost]);

  const response = data?.rtn?.SYSUserRequestNewPwd;

  const hasError = Boolean(
    response?.result === TEDSYSUserPwdResponse.ERROR || error
  );

  const requestNewPwdDisabled = props.gas || epost === '' || loading;

  return (
    <EDDialog
      open={props.open}
      onClose={() => {
        props.setOpen(false);
      }}
      title="Glömt lösenord?"
      footerActions={[
        {
          label: 'Stäng',
          onClick: () => {
            props.setOpen(false);
          },
        },
        ...(!response || hasError
          ? [
              {
                label: 'Skicka länk',
                buttonProps: {
                  disabled: requestNewPwdDisabled,
                  primary: true,
                  flat: false,
                },
                onClick: requestNewPwd,
              },
            ]
          : []),
      ]}
      onExited={() => {
        setEpost('');
      }}
    >
      {props.gas ? (
        <>
          <EDTypography>
            Du kan återställa eller ändra ditt lösenord för ditt Svenska
            kyrkan-konto via denna länk:
          </EDTypography>
          <br />
          <EDLink
            href="https://losenord.svenskakyrkan.se/"
            target="_blank"
            testId="link-losenord.svenskakyrkan"
          >
            losenord.svenskakyrkan.se
          </EDLink>
        </>
      ) : !response || hasError ? (
        <EDKeybind
          keymap="Enter"
          onKeyDown={() => {
            if (!requestNewPwdDisabled) {
              requestNewPwd();
            }
          }}
        >
          <EDTypography>
            Ange den e-postadress som är kopplad till ditt konto. Du kommer att
            få en länk skickad dit, där du kan välja ett nytt lösenord.
          </EDTypography>
          <br />
          <EDTypography>
            Databas:{' '}
            <EDTypography component="span" bold={true}>
              {props.dbname}
            </EDTypography>
          </EDTypography>
          <EDTextEdit
            onChange={newValue => {
              setEpost(newValue);
            }}
            value={epost}
            label="E-postadress"
            error={hasError}
            helperText={error?.message ?? response?.message ?? undefined}
            testId="epostGlömtLösenord"
          />
          {response && response?.result !== TEDSYSUserPwdResponse.OK && (
            <EDTypography>
              Om ingen e-postadress är registrerad på ditt konto måste du
              kontakta din systemadministratör för att kunna återställa
              lösenordet.
            </EDTypography>
          )}
        </EDKeybind>
      ) : (
        <EDTypography>
          Länk där du kan välja ett nytt lösenord har skickats till{' '}
          <EDTypography component="span" bold={true}>
            {epost}
          </EDTypography>
        </EDTypography>
      )}
    </EDDialog>
  );
};
